import { FormControl, MenuItem, StandardTextFieldProps, TextField } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import { RegistrationField, TextValueOption } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { RegistrationFormFieldProps } from '../RegistrationFormField';

interface RegistrationLanguageInterface extends Omit<RegistrationFormFieldProps, 'register' | 'watch'> {
  isRequired: (field: RegistrationField) => boolean;
  textFieldSx?: any;
}

const RegistrationLanguageField = ({
  field: registrationField,
  defaultValue,
  disabled,
  variant,
  textFieldSx,
  fieldsTextColor,
  fieldsBorderColor,
  updateRequiredFieldIds,
  requiredFieldIds,
  isRequired,
  showFieldLabel,
  onChange,
  onBlur,
}: RegistrationLanguageInterface) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { ct } = useCustomTranslations();

  const label = showFieldLabel !== false && registrationField.label ? ct(registrationField.label, registrationField, 'labelTranslations') : '';
  const event = useSelector((state) => state.common.event ?? state.admin.event);

  return (
    <Controller
      control={control}
      name={registrationField._id}
      key={registrationField._id}
      rules={{
        required: {
          value: isRequired(registrationField),
          message: t('form:fieldrequired'),
        },
      }}
      defaultValue={defaultValue || ''}
      render={({ field }) => (
        <FormControl fullWidth variant={variant}>
          <TextField
            inputRef={field.ref}
            name={field.name}
            onBlur={() => {
              if (onBlur) onBlur(registrationField._id, field.value);
              else field.onBlur();
            }}
            value={field.value}
            select
            color="secondary"
            error={!!errors[registrationField._id]}
            helperText={errors[registrationField._id]?.message?.toString()}
            variant={variant as StandardTextFieldProps['variant']}
            sx={{
              ...textFieldSx,
              color: 'blue',
              '.MuiInputBase-root': {
                color: fieldsTextColor || event?.template.mainColor,
              },
              '.MuiSvgIcon-root ': {
                fill: `${fieldsTextColor || event?.template.mainColor} !important`,
              },
            }}
            SelectProps={{ displayEmpty: true }}
            label={label}
            disabled={disabled}
            fullWidth
            id={`select-${field.value._id}`}
            onChange={(e) => {
              if (onChange) onChange(registrationField._id, e.target.value);
              else field.onChange(e.target?.value);

              const target = e.target?.value;

              updateRequiredFieldIds(
                new Map(requiredFieldIds).set(
                  registrationField._id,
                  (registrationField.textValueOptions as TextValueOption[])
                    ?.find((o) => o.content === target)
                    ?.conditionalRequiredFields?.map((f) => (f._id ? f._id : f)) || []
                )
              );
            }}>
            <MenuItem key="empty" value="" disabled>
              {t('common:please-select')}
            </MenuItem>
            {event?.languages?.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {t(`common:languages.${lang}`)}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      )}
    />
  );
};
export default RegistrationLanguageField;
